import { create } from "zustand";
import {
  fetchUsers,
  sendNotification,
  sendNotifications,
  subscribeToPush,
  unsubscribeFromPush,
} from "../services/pushService";
import type { User } from "../types/push";

type PushStore = {
  users: User[];
  loading: boolean;
  error: string | null;
  isSubscribed: boolean;
  loadUsers: () => Promise<void>;
  clearUsers: () => void;
  subscribeToPush: (reg: ServiceWorkerRegistration) => Promise<void>;
  unsubscribeFromPush: (reg: ServiceWorkerRegistration) => Promise<void>;
  sendUserNotification: (userId: string, message: string) => Promise<boolean>;
  sendAllNotifications: (message: string) => Promise<boolean>;
};

export const usePushStore = create<PushStore>((set) => ({
  users: [],
  loading: false,
  error: null,
  isSubscribed: false,

  loadUsers: async () => {
    set({ loading: true, error: null });
    try {
      const users = await fetchUsers();
      if (users) {
        set({ users });
      } else {
        set({ error: "Failed to fetch users." });
      }
    } catch (err) {
      console.error("An error occurred while loading users:", err);
      set({ error: "An error occurred while loading users." });
    } finally {
      set({ loading: false });
    }
  },

  clearUsers: () => {
    set({ users: [], error: null });
  },

  subscribeToPush: async (reg) => {
    set({ loading: true, error: null });
    try {
      const success = await subscribeToPush(reg);
      const sub = await reg.pushManager.getSubscription();
      set({ isSubscribed: !!sub });
  
      if (!success) {
        set({ error: "Subscription to push failed." });
      }
    } catch (err) {
      console.error("Error subscribing to push:", err);
      set({ error: "An error occurred during push subscription." });
    } finally {
      set({ loading: false });
    }
  },
  
  unsubscribeFromPush: async (reg) => {
    set({ loading: true, error: null });
    try {
      const success = await unsubscribeFromPush(reg);
      const sub = await reg.pushManager.getSubscription();
      set({ isSubscribed: !!sub });
      if (!success) {
        set({ error: "Unsubscription from push failed." });
      }
    } catch (err) {
      console.error("Error unsubscribing from push:", err);
      set({ error: "An error occurred during push unsubscription." });
    } finally {
      set({ loading: false });
    }
  },

  sendUserNotification: async (userId: string, message: string) => {
    try {
      const success = await sendNotification(userId, message);
      if (!success) {
        console.warn(`Notification failed for user: ${userId}`);
      }
      return success;
    } catch (err) {
      console.error(`Error notifying user ${userId}:`, err);
      return false;
    }
  },

  sendAllNotifications: async (message: string) => {
    try {
      const data = await sendNotifications(message);
  
      if (data?.code === 1107) {
        console.info("✅ Notifications sent to all users successfully.");
        return true;
      } else {
        console.warn("⚠️ Notification failed with server response:", data);
        return false;
      }
  
    } catch (err) {
      console.error("❌ Error sending notifications to all users:", err);
      return false;
    }
  },
}));
